/**
 * Lightweight cross-cutting chat event bus.
 *
 * `ChatWidget` lives deep in the tree and owns the streaming/save lifecycle,
 * while the onboarding coach renders at app level. This tiny pub/sub lets
 * ChatWidget broadcast the two signals the tour cares about without either
 * component depending on the other:
 *   - `firstReplyReceived`  → the user's first Sage answer arrived (step 4)
 *   - `saveConfirmed`       → the first session was saved with an ID token (step 5, tour complete)
 */

export type ChatStateEvent = {
  firstReplyReceived?: boolean;
  saveConfirmed?: boolean;
};

type Listener = (event: ChatStateEvent) => void;

const listeners = new Set<Listener>();

export function subscribeChatState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitChatState(event: ChatStateEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch {
      /* a single bad listener must not break the chat flow */
    }
  }
}
